import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_REMINDER_PREFS,
  formatDateTime,
  type CreditReminder,
  type CreditReminderPrefs,
  type CreditSettings,
  type CreditChannel,
} from '@/lib/credit';
import QrModal from './QrModal';
import { useMerchantCurrency } from '@/hooks/useMerchantCurrency';

interface Props {
  settings: CreditSettings | null;
  currency: string;
  reminders: CreditReminder[];
  merchantId: number;
  onSave: (patch: Partial<CreditSettings>) => Promise<void>;
}

const CHANNELS: { value: CreditChannel; labelKey: string; icon: string }[] = [
  { value: 'inapp', labelKey: 'crf.channel_inapp', icon: 'ri-smartphone-line' },
];

const REMINDER_TYPES: { key: keyof Omit<CreditReminderPrefs, 'channel'>; labelKey: string }[] = [
  { key: 'new_credit', labelKey: 'crf.reminder_new_credit' },
  { key: 'payment_confirmation', labelKey: 'crf.reminder_payment' },
  { key: 'balance', labelKey: 'crf.reminder_balance' },
  { key: 'due_date', labelKey: 'crf.reminder_due_date' },
  { key: 'monthly_statement', labelKey: 'crf.reminder_monthly' },
];

export default function SettingsView({ settings, currency, reminders, merchantId, onSave }: Props) {
  const { t } = useTranslation();
  // Devise unique du marchand : le carnet de crédit s'aligne dessus.
  const merchantCurrency = useMerchantCurrency();
  const cur = merchantCurrency || currency;
  const [dueDay, setDueDay] = useState(settings?.payment_due_day != null ? String(settings.payment_due_day) : '');
  const [prefs, setPrefs] = useState<CreditReminderPrefs>(settings?.reminders || DEFAULT_REMINDER_PREFS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const joinUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/credit/join?merchant=${merchantId}`;

  const toggle = (key: keyof Omit<CreditReminderPrefs, 'channel'>) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        currency: cur,
        payment_due_day: dueDay ? parseInt(dueDay, 10) : null,
        reminders: prefs,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Currency */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1">{t('crf.currency', { defaultValue: 'Devise' })}</h3>
        <p className="text-xs text-foreground-400 mb-3">{t('crf.currency_desc', { defaultValue: 'Utilisée sur tout le carnet de crédit.' })}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1">{t('crf.currency', { defaultValue: 'Devise' })}</label>
            <div className="flex items-center gap-2 w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-100 text-sm text-foreground-700" title="Devise unique de votre compte">
              <i className="ri-lock-line text-foreground-400"></i>
              <span className="font-medium">{cur}</span>
            </div>
            <p className="text-[11px] text-foreground-400 mt-1">{t('crf.currency_hint', { defaultValue: 'Définie dans Paramètres → Profil.' })}</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1">{t('crf.default_due_day', { defaultValue: 'Jour d\u2019échéance par défaut' })}</label>
            <select value={dueDay} onChange={(e) => setDueDay(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300">
              <option value="">{t('crf.no_due_day', { defaultValue: 'Non défini' })}</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{t('crf.day_of_month', { defaultValue: 'Le {day} du mois', day: d })}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Reminders */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-1">{t('crf.reminders', { defaultValue: 'Rappels' })}</h3>
        <p className="text-xs text-foreground-400 mb-3">
          {t('crf.reminders_desc', { defaultValue: "Les rappels sont envoyés à votre client via son application Zifek (PWA), s'il l'a installée. S'il ne l'a pas installée, aucun rappel automatique n'est envoyé." })}
        </p>
        <div className="space-y-2.5 mb-4">
          {REMINDER_TYPES.map((r) => (
            <label key={r.key} className="flex items-center justify-between gap-3 cursor-pointer">
              <span className="text-sm text-foreground-700">{t(r.labelKey, { defaultValue: r.labelKey })}</span>
              <button
                type="button"
                onClick={() => toggle(r.key)}
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 cursor-pointer ${prefs[r.key] ? 'bg-primary-500' : 'bg-background-200'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${prefs[r.key] ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </label>
          ))}
        </div>
        <div>
          <label className="block text-xs font-semibold text-foreground-600 mb-2">{t('crf.default_channel', { defaultValue: 'Canal par défaut' })}</label>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setPrefs((p) => ({ ...p, channel: c.value }))}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                  prefs.channel === c.value ? 'bg-secondary-500 text-background-50 border-secondary-500' : 'bg-background-50 border-background-200/70 text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className={c.icon}></i>{t(c.labelKey, { defaultValue: c.labelKey })}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Merchant QR */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground-900 mb-1">{t('crf.merchant_qr', { defaultValue: 'QR d\u2019invitation marchand' })}</h3>
          <p className="text-xs text-foreground-400 max-w-md">
            {t('crf.merchant_qr_desc', { defaultValue: 'Affichez ce code en caisse. Vos clients connectés à Zifek le scannent pour lier leur compte à votre carnet.' })}
          </p>
        </div>
        <button onClick={() => setShowQr(true)}
          className="h-11 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center justify-center gap-2 flex-shrink-0">
          <i className="ri-qr-code-line"></i>{t('crf.show_qr', { defaultValue: 'Afficher le QR' })}
        </button>
      </div>

      {/* Reminders log */}
      {reminders.length > 0 && (
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3">{t('crf.last_reminders', { defaultValue: 'Derniers rappels' })}</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {reminders.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-foreground-700 truncate">{r.type.replace(/_/g, ' ')} · {r.channel}</span>
                <span className="text-[11px] text-foreground-400 flex-shrink-0">{formatDateTime(r.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={handleSave} disabled={saving}
        className="h-11 px-6 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50">
        {saving ? <><i className="ri-loader-4-line animate-spin"></i>{t('crf.saving', { defaultValue: 'Enregistrement...' })}</> : saved ? <><i className="ri-check-line"></i>{t('crf.saved', { defaultValue: 'Enregistré !' })}</> : <><i className="ri-save-line"></i>{t('crf.save_settings', { defaultValue: 'Enregistrer les réglages' })}</>}
      </button>

      {showQr && (
        <QrModal title="Invitation marchand" subtitle="Lien de rattachement client" data={joinUrl} onClose={() => setShowQr(false)} />
      )}
    </div>
  );
}